import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import WorkPage from "./pages/WorkPage";
import PortfolioPage from "./pages/PortfolioPage";
import ContactPage from "./pages/ContactPage";
import InteractionDesignCase from "./pages/InteractionDesignCase";
import EventManagementCaseStudy from "./pages/EventManagementCaseStudy";
import STQM from "./pages/STQM";
import ParticlesBackground from "./components/ParticlesBackground";
import { useTheme } from "./context/ThemeContext";
import { Moon, Sun, Menu } from 'lucide-react';

// Responsive device hook
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < breakpoint);
  React.useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < breakpoint); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
}

// Mobile navbar overlay (full height, scrollable, theme-aware)
function MobileNavMenu({ navs, location, open, setOpen }) {
  const { theme } = useTheme();

  if (!open) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      right: 0,
      width: "80vw",
      maxWidth: 320,
      height: "100vh",
      background: theme === "dark" ? "#0f172a" : "#ffffff",
      color: theme === "dark" ? "#e2e8f0" : "#232751",
      boxShadow: theme === "dark" ? "-8px 0 40px rgba(0,0,0,0.6)" : "-8px 0 40px rgba(0,0,0,0.15)",
      zIndex: 9999,
      paddingTop: "80px", // extra space untuk notch/status bar
      paddingBottom: "40px",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      overflowY: "auto",
      transition: "transform 0.3s ease",
      transform: open ? "translateX(0)" : "translateX(100%)",
    }}>
      <button
        onClick={() => setOpen(false)}
        style={{
          background: "none",
          border: "none",
          color: theme === "dark" ? "#a5d6ff" : "#7b9acc",
          fontSize: 40,
          fontWeight: 900,
          marginRight: 24,
          marginTop: -20,
          marginBottom: 32,
          cursor: "pointer",
        }}
      >
        ×
      </button>

      {navs.map(({ to, label }) => (
        <Link
          key={to}
          to={to}
          onClick={() => setOpen(false)}
          style={{
            width: "100%",
            textAlign: "right",
            color: location.pathname === to
              ? (theme === "dark" ? "#a5d6ff" : "#7b9acc")
              : (theme === "dark" ? "#cbd5e1" : "#4b5563"),
            textDecoration: "none",
            padding: "16px 24px",
            fontWeight: location.pathname === to ? 900 : 700,
            fontSize: 18,
            opacity: location.pathname === to ? 1 : 0.9,
            background: location.pathname === to
              ? (theme === "dark" ? "rgba(165,214,255,0.12)" : "rgba(123,154,204,0.08)")
              : "transparent",
            transition: "all 0.2s ease",
          }}
        >
          {label}
        </Link>
      ))}
    </div>
  );
}

// Navbar (mobile-optimized)
function Navbar() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [showMenu, setShowMenu] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const navs = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/work", label: "Work" },
    { to: "/portfolio", label: "Projects" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      <nav
        style={{
          width: "100vw",
          height: isMobile ? "64px" : "72px",
          background: theme === "dark"
            ? "rgba(13, 17, 23, 0.92)"   // ← kembali ke warna asli dark
            : "rgba(255, 255, 255, 0.92)", // ← kembali ke warna asli light
          backdropFilter: "blur(14px)",
          boxShadow: theme === "dark"
            ? "0 6px 32px rgba(0,0,0,0.6)"
            : "0 6px 32px rgba(0,0,0,0.12)",
          // borderBottom dihapus total → tanpa garis biru atau garis apa pun di bawah
          padding: isMobile ? "0 16px" : "0 32px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1000,
          transition: "all 0.3s ease",
        }}
      >
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          aria-label="Toggle theme"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: isMobile ? "10px" : "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme === "dark" ? "#a5d6ff" : "#7b9acc",
            position: "absolute",
            left: isMobile ? "12px" : "32px",
            top: "50%",
            transform: "translateY(-50%)",
            transition: "transform 0.3s ease",
            fontSize: isMobile ? "1.3rem" : "1.4rem",
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.15) translateY(-50%)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1) translateY(-50%)"}
        >
          {theme === "dark" ? <Sun size={isMobile ? 24 : 28} /> : <Moon size={isMobile ? 24 : 28} />}
        </button>

        {/* Desktop links */}
        {!isMobile && (
          <div style={{ display: "flex", gap: "3rem", justifyContent: "center" }}>
            {navs.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                style={{
                  color: location.pathname === to
                    ? (theme === "dark" ? "#a5d6ff" : "#7b9acc")
                    : (theme === "dark" ? "#e2e8f0" : "#232751"),
                  textDecoration: "none",
                  fontWeight: location.pathname === to ? 900 : 700,
                  fontSize: 18,
                  transition: "all 0.22s ease",
                  transform: location.pathname === to ? "scale(1.08)" : "scale(1)",
                }}
              >
                {label}
              </Link>
            ))}
          </div>
        )}

        {/* Mobile menu button - posisi agak ke tengah-kiri */}
        {isMobile && (
          <button
            onClick={() => setShowMenu(v => !v)}
            style={{
              background: "none",
              border: "none",
              fontSize: 28,
              fontWeight: 900,
              color: theme === "dark" ? "#a5d6ff" : "#7b9acc",
              cursor: "pointer",
              padding: 0,
              lineHeight: 1,
              position: "absolute",
              right: "40px",  // ← posisi tetap seperti yang kamu suka (agak ke tengah-kiri)
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <Menu size={28} />
          </button>
        )}
      </nav>

      <MobileNavMenu
        navs={navs}
        location={location}
        open={isMobile && showMenu}
        setOpen={setShowMenu}
      />
    </>
  );
}
// Main App
export default function App() {
  const isMobile = useIsMobile();

  return (
    <Router>
      <ParticlesBackground />

      <Navbar />

      <div
        className="page-content"
        style={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          background: "transparent",
          paddingTop: isMobile ? "74px" : "90px", // navbar height + sedikit margin
          paddingBottom: "20px",
        }}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/work" element={<WorkPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/interaction-design-case" element={<InteractionDesignCase />} />
          <Route path="/event-management-case-study" element={<EventManagementCaseStudy />} />
          <Route path="/stqm-case-study" element={<STQM />} />
        </Routes>
      </div>
    </Router>
  );
}